/**
 * Modo Demonstração — máscara em tempo real sobre dados sensíveis.
 *
 * Quando ativado (localStorage.demo_mode === "on"), substitui:
 *   • Nome da empresa (Cebolas Serenata Ltda → Hortifruti Demo Ltda)
 *   • Nome/CNPJ de clientes reais (por Cliente #N ficticio)
 *   • Nome/CNPJ de fornecedores (por Fornecedor #N)
 *   • Nome de motoristas / usuários reais (por Motorista #N)
 *
 * NÃO altera dados no banco — só na renderização. Ao desligar, volta ao normal.
 */

const DEMO_KEY = "dataneres_demo_mode";

// Empresa fictícia — pode ser customizada pela tela de configurações
const DEMO_COMPANY = {
  nome_fantasia: "Hortifruti Demo Ltda",
  razao_social: "Hortifruti Demo Comércio Atacadista Ltda",
  cnpj: "12.345.678/0001-99",
  ie: "123.456.789.000",
  cidade: "São Paulo",
  uf: "SP",
  endereco: "Rua da Demonstração, 100",
  telefone: "(11) 3456-7890",
};

const DEMO_CLIENTES = [
  "Rede Mercado Verde", "Fazenda São Bento", "Atacadão Central FLV",
  "Distribuidora Horti Sul", "Supermercados Boa Safra", "Comércio Frutas Nobres",
  "Central de Abastecimento Norte", "Mercearia Terra Boa", "Ceasa Distribuidora",
  "Verduras Premium Ltda",
];

const DEMO_FORNECEDORES = [
  "Fazenda Aurora", "Produtor Rural do Vale", "Cooperativa Nova Terra",
  "Chácara Bela Vista", "Sítio Vista Alegre", "Fazenda Boa Colheita",
];

const DEMO_MOTORISTAS = [
  "João Silva", "Pedro Santos", "Carlos Oliveira", "Marcos Ferreira",
  "Luiz Rodrigues", "Antonio Costa",
];

// Cache determinístico: mesmo texto original → mesmo pseudônimo (estável entre renderizações)
const _cache = new Map();

function hashPick(str, list) {
  if (!str) return list[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return list[Math.abs(h) % list.length];
}

export function isDemoOn() {
  try { return localStorage.getItem(DEMO_KEY) === "on"; } catch (_) { return false; }
}

export function setDemo(on) {
  try {
    if (on) localStorage.setItem(DEMO_KEY, "on");
    else localStorage.removeItem(DEMO_KEY);
    // Dispara evento pra qualquer componente reagir
    window.dispatchEvent(new Event("demo-mode-change"));
  } catch (_) { /* ignore */ }
}

/** Mascara nome de cliente */
export function maskCliente(nome) {
  if (!isDemoOn() || !nome) return nome;
  if (_cache.has("cli:" + nome)) return _cache.get("cli:" + nome);
  const alias = hashPick(nome, DEMO_CLIENTES);
  _cache.set("cli:" + nome, alias);
  return alias;
}

/** Mascara nome de fornecedor */
export function maskFornecedor(nome) {
  if (!isDemoOn() || !nome) return nome;
  if (_cache.has("for:" + nome)) return _cache.get("for:" + nome);
  const alias = hashPick(nome, DEMO_FORNECEDORES);
  _cache.set("for:" + nome, alias);
  return alias;
}

/** Mascara nome de motorista */
export function maskMotorista(nome) {
  if (!isDemoOn() || !nome) return nome;
  if (_cache.has("mot:" + nome)) return _cache.get("mot:" + nome);
  const alias = hashPick(nome, DEMO_MOTORISTAS);
  _cache.set("mot:" + nome, alias);
  return alias;
}

/** Mascara CNPJ (mantém formato) */
export function maskCNPJ(cnpj) {
  if (!isDemoOn() || !cnpj) return cnpj;
  return DEMO_COMPANY.cnpj;
}

/** Mascara nome da empresa (razão social / fantasia) */
export function maskEmpresa(nome, tipo = "fantasia") {
  if (!isDemoOn() || !nome) return nome;
  return tipo === "razao" ? DEMO_COMPANY.razao_social : DEMO_COMPANY.nome_fantasia;
}

/** Mascara placa (mantém formato mas gera fictícia) */
export function maskPlaca(placa) {
  if (!isDemoOn() || !placa) return placa;
  if (_cache.has("plc:" + placa)) return _cache.get("plc:" + placa);
  const letters = "ABC DEF GHI JKL MNO PQR STU VWX YZA".split(" ");
  let h = 0;
  for (let i = 0; i < placa.length; i++) h = ((h << 5) - h + placa.charCodeAt(i)) | 0;
  const l = letters[Math.abs(h) % letters.length];
  const n = String(Math.abs(h) % 9000 + 1000);
  const alias = `${l}-${n.slice(0,1)}${l[1]}${n.slice(1,3)}`;
  _cache.set("plc:" + placa, alias);
  return alias;
}

/** Endereço mascarado */
export function maskEndereco(endereco) {
  if (!isDemoOn()) return endereco;
  return DEMO_COMPANY.endereco;
}

export const DEMO_META = DEMO_COMPANY;
